import { Activity, Upload, Settings as SettingsIcon, LogOut, User as UserIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { BRAND } from "@/lib/designTokens";
import { TopNavigation } from "@/components/TopNavigation";
import { useCoachPendingAction } from "@/hooks/useCoachPendingAction";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const AppHeader = ({ onUploadClick, onOpenMobileNav, showMobileMenu = true }) => {
  const { user, logout } = useAuth();
  const { pendingCount: coachPendingCount } = useCoachPendingAction();

  return (
    <header className="app-header sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:backdrop-blur">
      <div className="flex items-center gap-3 lg:gap-6 px-4 sm:px-6 lg:px-8 py-3 min-w-0 min-h-[4rem]">
        <div className="flex items-center gap-3 min-w-0 shrink-0">
          {showMobileMenu ? (
            <button
              type="button"
              onClick={onOpenMobileNav}
              className="md:hidden btn-icon-app focus-visible:outline-none"
              aria-label="Open navigation menu"
              data-testid="mobile-nav-open"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
              </svg>
            </button>
          ) : null}

          <Link
            to="/"
            className="flex items-center gap-3 min-w-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
            data-testid="app-logo-link"
          >
            <div className="w-9 h-9 rounded-md border border-brand-primary flex items-center justify-center bg-brand-primary/10 shrink-0">
              <Activity className="w-4 h-4 text-brand-primary" strokeWidth={1.5} aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <div className="font-heading text-lg lg:text-xl leading-none tracking-wider uppercase truncate">
                {BRAND.productName}
              </div>
              <div className="text-[10px] tracking-[0.12em] text-dark-text-muted uppercase mt-0.5 truncate">
                {BRAND.companyName}
              </div>
            </div>
          </Link>
        </div>

        <TopNavigation
          className="flex-1 justify-end xl:justify-start xl:ml-4"
          coachPendingCount={coachPendingCount}
        />

        <div className="flex items-center gap-2 sm:gap-3 shrink-0 ml-auto md:ml-0">
          <button
            type="button"
            onClick={onUploadClick}
            aria-label="Upload activity"
            className="btn-primary-app px-3 sm:px-4 py-2.5 text-xs tracking-[0.15em] uppercase rounded-md min-h-[44px] min-w-[44px]"
            data-testid="header-upload-button"
          >
            <Upload className="w-4 h-4 shrink-0" strokeWidth={2} aria-hidden="true" />
            <span className="hidden sm:inline">Upload</span>
          </button>

          <ThemeToggle />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="User menu"
                className="btn-icon-app overflow-hidden focus-visible:outline-none"
                data-testid="user-menu-trigger"
              >
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <UserIcon className="w-4 h-4 text-dark-text-muted" strokeWidth={1.5} aria-hidden="true" />
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-dark-surface-1 border border-dark-border rounded-md min-w-[220px] shadow-app-md"
            >
              <div className="px-3 py-2">
                <div className="text-sm text-dark-text-primary truncate">{user?.name}</div>
                <div className="text-[11px] text-dark-text-muted font-mono truncate">{user?.email}</div>
              </div>
              <DropdownMenuSeparator className="bg-dark-border" />
              <DropdownMenuItem asChild>
                <Link
                  to="/settings"
                  className="text-xs tracking-widest uppercase rounded-md cursor-pointer flex items-center gap-2 min-h-[44px]"
                  data-testid="menu-settings-link"
                >
                  <SettingsIcon className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={logout}
                className="text-xs tracking-widest uppercase rounded-md cursor-pointer flex items-center gap-2 text-analytics-red focus:text-analytics-red min-h-[44px]"
                data-testid="menu-logout"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
